import { useState, useEffect, useCallback, FormEvent, ChangeEvent } from 'react';
import BillingCategoryService from '../../AppSettings/services/BillingCategoryService';
import toastService from '../../AppSettings/services/ToastService';
import { useError } from '../../Context/ErrorContext';
import { FIELD_SERVICES_CATEGORY_CODE } from '../../Financial/timesheetCategories';
import type { BillingCategory } from '../../Models/Rate/RateModels';
import type { ErrorContextValue } from '../../types/team';

interface CostCodesManagerProps {
  onChanged?: () => void;
}

// Cost codes are stored in time-entry notes and recovered by a leading 3-digit regex,
// so new codes are constrained to exactly 3 digits to keep every consumer parseable.
function isThreeDigitCode(value: string): boolean {
  return /^\d{3}$/.test(value.trim());
}

/**
 * Admin surface for the cost-code catalog (billing_categories). Embedded inside the Company Rates
 * modal. A code added here auto-appears on every project's timesheet and budget editor, because
 * the grids and budgets are data-driven. Code 900 (Field Services) is reserved (bills at field
 * rate) and cannot be deleted.
 */
export const CostCodesManager = ({
  onChanged,
}: CostCodesManagerProps): JSX.Element => {
  const [categories, setCategories] = useState<BillingCategory[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [newCode, setNewCode] = useState<string>('');
  const [newDescription, setNewDescription] = useState<string>('');
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [editingDescription, setEditingDescription] = useState<string>('');

  const { setError } = useError() as unknown as ErrorContextValue;

  const fetchCategories = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const data = await BillingCategoryService.getCategories();
      setCategories(
        [...data].sort((a, b) => String(a.categoryid).localeCompare(String(b.categoryid))),
      );
    } catch (error) {
      console.error('Error fetching cost codes:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const notifyChanged = (): void => {
    onChanged?.();
  };

  const handleCreate = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    const code = newCode.trim();
    const description = newDescription.trim();
    if (!code || !description) return;
    if (!isThreeDigitCode(code)) {
      toastService.error('Cost code must be exactly 3 digits (e.g. 100).');
      return;
    }
    setSubmitting(true);
    try {
      await BillingCategoryService.createCategory({ categoryid: code, category: description });
      setNewCode('');
      setNewDescription('');
      await fetchCategories();
      notifyChanged();
      toastService.success(`Cost code ${code} added.`);
    } catch (error: unknown) {
      const status = (error as { status?: number })?.status;
      if (status === 409) {
        toastService.error(`Cost code ${code} already exists.`);
      } else {
        console.error('Error creating cost code:', error);
        setError(error);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartEdit = (category: BillingCategory): void => {
    setEditingCode(category.categoryid);
    setEditingDescription(category.category);
  };

  const handleCancelEdit = (): void => {
    setEditingCode(null);
    setEditingDescription('');
  };

  const handleSaveEdit = async (categoryId: string): Promise<void> => {
    const description = editingDescription.trim();
    if (!description) return;
    try {
      await BillingCategoryService.updateCategory(categoryId, { category: description });
      setEditingCode(null);
      setEditingDescription('');
      await fetchCategories();
      notifyChanged();
    } catch (error) {
      console.error('Error updating cost code:', error);
      setError(error);
    }
  };

  const handleDelete = async (categoryId: string): Promise<void> => {
    try {
      await BillingCategoryService.deleteCategory(categoryId);
      await fetchCategories();
      notifyChanged();
    } catch (error) {
      console.error('Error deleting cost code:', error);
      setError(error);
    }
  };

  return (
    <>
      <p className="team-rates-pane-intro">
        Three-digit codes used on timesheets and when attaching categories to a rate.
        Code 900 (Field Services) is reserved and cannot be deleted.
      </p>

      <form onSubmit={handleCreate} className="team-rates-form">
        <div className="team-rates-form-row">
          <input
            type="text"
            inputMode="numeric"
            maxLength={3}
            placeholder="Code (e.g. 150)"
            value={newCode}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setNewCode(e.target.value.replace(/\D/g, '').slice(0, 3))
            }
            required
            className="team-rates-input team-rates-input-rate"
          />
          <input
            type="text"
            placeholder="Description (e.g. Survey Work)"
            value={newDescription}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setNewDescription(e.target.value)
            }
            required
            className="team-rates-input"
          />
          <button
            type="submit"
            className="team-rates-add-button"
            disabled={submitting}
          >
            {submitting ? 'Adding...' : 'Add Code'}
          </button>
        </div>
      </form>

      <div className="team-rates-list">
        {loading ? (
          <div className="team-rates-loading">Loading cost codes...</div>
        ) : categories.length === 0 ? (
          <div className="team-rates-empty">
            No cost codes configured yet. Add your first code above.
          </div>
        ) : (
          <table className="team-rates-table team-rates-table--codes">
            <thead>
              <tr>
                <th>Code</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((category) => {
                const isReserved = category.categoryid === FIELD_SERVICES_CATEGORY_CODE;
                return (
                  <tr key={category.categoryid}>
                    {editingCode === category.categoryid ? (
                      <>
                        <td>{category.categoryid}</td>
                        <td>
                          <input
                            type="text"
                            value={editingDescription}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                              setEditingDescription(e.target.value)
                            }
                            className="team-rates-edit-input"
                          />
                        </td>
                        <td>
                          <div className="team-rates-action-buttons">
                            <button
                              className="team-rates-save-button"
                              onClick={() => handleSaveEdit(category.categoryid)}
                            >
                              Save
                            </button>
                            <button
                              className="team-rates-cancel-button"
                              onClick={handleCancelEdit}
                            >
                              Cancel
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td>{category.categoryid}</td>
                        <td>
                          {category.category}
                          {isReserved && (
                            <span className="team-rates-reserved-tag"> (reserved · field rate)</span>
                          )}
                        </td>
                        <td>
                          <div className="team-rates-action-buttons">
                            <button
                              className="team-rates-edit-button"
                              onClick={() => handleStartEdit(category)}
                            >
                              Edit
                            </button>
                            <button
                              className="team-rates-delete-button"
                              onClick={() => handleDelete(category.categoryid)}
                              disabled={isReserved}
                              title={
                                isReserved
                                  ? 'The Field Services (900) code is reserved and cannot be deleted.'
                                  : undefined
                              }
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
};
